import { type ApolloClient, gql } from '@apollo/client/core';
import type {
  ApplyTemplateMutationInput,
  CreateSheetVersionMutationInput,
  CreateTemplateMutationInput,
  DeleteContactMutationInput,
  DeleteExpenseCategoryMutationInput,
  DeleteExpenseMutationInput,
  DeleteListMutationInput,
  DeleteTicketMutationInput,
  GeneratePerformanceDatesMutationInput,
  LockSheetMutationInput,
  Mutation,
  Query,
  Sheet as GQLSheet,
  UpdateSheetMutationInput,
  UpsertAgentMutationInput,
  UpsertContactMutationInput,
  UpsertExpenseCategoryMutationInput,
  UpsertExpenseMutationInput,
  UpsertListMutationInput,
  UpsertTicketMutationInput,
} from '../../../generated/api.graphql';
import { DealFigureEnum } from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import type { VPResult } from '../../VPResult';
import * as Agent from './Agent';
import * as Expense from './Expense';
import * as ExpenseCategory from './ExpenseCategory';
import * as List from './List';
import * as OfferSheetContact from './OfferSheetContact';
import * as OfferTicket from './OfferTicket';
import * as Sheet from './Sheet';
import * as SheetVersion from './SheetVersion';
import * as Summary from './Summary';
import * as Template from './Template';
import {
  AGENT_FIELDS,
  EXPENSE_CATEGORY_FIELDS,
  EXPENSE_FIELDS,
  LIST_FIELDS,
  OFFER_SHEET_CONTACT_FIELDS,
  OFFER_TICKET_FIELDS,
  SHEET_FIELDS,
  SUMMARY_FIELDS,
  TEMPLATE_FIELDS,
} from './fragments';

/**
 * The offer sheet's whole surface on the tenant (AppSchema) connection: two reads, five
 * sheet-level mutations, ten line-item mutations and two template mutations. The legacy
 * equivalents are the root `offerSheet` / `templates` fields and the members of the
 * `offerSheet { … }` mutation namespace, all deprecated; the AppSchema mutations are **root
 * fields**, which is why nothing here nests a selection under `offerSheet`.
 *
 * **Nothing here catches.** A transport failure throws and propagates; `VPResult`'s
 * `success: false` covers only the GraphQL-level outcome of the mutations that report errors.
 * That preserves the admin store's `try`/`finally`-with-no-`catch` -- this is a transport move,
 * not a change of failure semantics.
 *
 * **Two fields of the sheet are not here.** AppSchema's `Sheet` carries neither `htmlContent` nor
 * `pdfDownloadUrl`, and the builder needs both, so they are read from the legacy connection by
 * `api/Offer/Api.ts` -- in the legacy namespace, where legacy `gql` belongs. Fold that selection
 * back into `SHEET_FIELDS` and delete that module, together with `@vp/admin`'s
 * `composables/useOfferSheetRendering.ts`, once the backend publishes the fields here.
 *
 * **A payload that carries `errors` returns a `VPResult`.** Among the five sheet-level mutations
 * only `upsertOfferSheetAgent` does; the other four resolve `{ clientMutationId, sheet }` or
 * `{ clientMutationId, version }` and so return their entity directly. All twelve line-item and
 * template mutations carry `errors`, so all twelve return a `VPResult` -- which is what lets the
 * store leave live state alone on a failed write instead of assigning `undefined` over it.
 *
 * **Every mutation that can move the totals selects `summary`.** That is the point of 7.8b: the
 * caller takes the recomputed totals off the write it just made rather than going back for them,
 * which is what 7.7b's `reloadSummary()` follow-up read existed to do. Two sheet-returning
 * mutations deliberately do not select it, and neither ever refreshed the summary: locking a sheet
 * stamps `lockedAt` and moves no figure, and applying a template overwrites the offer so completely
 * that its caller drops the sheet and re-reads it whole.
 *
 * **The selection sets are deliberately asymmetric.** `UPDATE_OFFER_SHEET_MUTATION` selects the
 * whole sheet because its caller merges the payload into state wholesale, and `Sheet.fromGql`
 * writes every nested key unconditionally -- a narrow selection there would blank the lists,
 * tickets, contacts and expense categories after each save. Every other sheet-returning mutation
 * selects only the collection its caller assigns, plus the summary, and their return types say
 * so. Do not "fix" the asymmetry by widening the merge.
 */

const failure = (message: string): VPResult<never, VPErrors.VPError[]> => ({
  success: false,
  errors: [{ path: '', message }],
});

/** The mapped summary of a mutation payload's sheet, or `undefined` when the payload has neither. */
const summaryOf = (sheet?: Pick<GQLSheet, 'summary'> | null): Summary.Summary | undefined =>
  sheet?.summary ? Summary.fromGql(sheet.summary) : undefined;

/**
 * What a line-item write hands back. These are deliberately **not** `Pick<Sheet, …>`: `Sheet`
 * mirrors the schema's nullability, where every collection is optional, whereas a successful write
 * always resolves one -- empty when the sheet has no rows left. Picking off `Sheet` would push that
 * `| undefined` onto every caller for a case that cannot happen, and the admin store's own state
 * declares these collections required.
 *
 * The counts stay optional: they really are nullable on the schema, and a caller that does not get
 * one keeps the count it had.
 */
type SummaryResult = { summary?: Summary.Summary };
type ContactsResult = SummaryResult & { contacts: OfferSheetContact.OfferSheetContact[] };
type ExpenseCategoriesResult = SummaryResult & { expenseCategories: ExpenseCategory.ExpenseCategory[] };
type ListsResult = SummaryResult & { listCount?: number; lists: List.List[] };
type TicketsResult = SummaryResult & { ticketCount?: number; tickets: OfferTicket.OfferTicket[] };

export const OFFER_SHEET_QUERY = gql`
  query AppOfferSheet($id: Int!) {
    offerSheet(id: $id) {
      ...SheetFields
    }
  }
  ${SHEET_FIELDS}
`;

export const OFFER_SHEET_TEMPLATES_QUERY = gql`
  query AppOfferSheetTemplates {
    offerSheetTemplates {
      ...TemplateFields
    }
  }
  ${TEMPLATE_FIELDS}
`;

export const UPDATE_OFFER_SHEET_MUTATION = gql`
  mutation updateOfferSheet($input: UpdateSheetMutationInput!) {
    updateOfferSheet(input: $input) {
      sheet {
        ...SheetFields
      }
    }
  }
  ${SHEET_FIELDS}
`;

export const LOCK_OFFER_SHEET_MUTATION = gql`
  mutation lockOfferSheet($input: LockSheetMutationInput!) {
    lockOfferSheet(input: $input) {
      sheet {
        id
        lockedAt
        lockedBy {
          name
        }
      }
    }
  }
`;

export const CREATE_OFFER_SHEET_VERSION_MUTATION = gql`
  mutation createOfferSheetVersion($input: CreateSheetVersionMutationInput!) {
    createOfferSheetVersion(input: $input) {
      version {
        id
        version
        versionName
      }
    }
  }
`;

export const GENERATE_OFFER_SHEET_PERFORMANCE_DATES_MUTATION = gql`
  mutation generateOfferSheetPerformanceDates($input: GeneratePerformanceDatesMutationInput!) {
    generateOfferSheetPerformanceDates(input: $input) {
      sheet {
        id
        performanceDatesDescription
        summary {
          ...SummaryFields
        }
      }
    }
  }
  ${SUMMARY_FIELDS}
`;

export const UPSERT_OFFER_SHEET_AGENT_MUTATION = gql`
  mutation upsertOfferSheetAgent($input: UpsertAgentMutationInput!) {
    upsertOfferSheetAgent(input: $input) {
      sheet {
        id
        agent {
          ...AgentFields
        }
        summary {
          ...SummaryFields
        }
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${AGENT_FIELDS}
  ${SUMMARY_FIELDS}
  ${VP_ERROR_FIELDS}
`;

/**
 * The payload also carries the saved `contact`, and this deliberately does not ask for it: the
 * caller replaces its contact list wholesale and then finds the saved row in it by id or email.
 * Selecting a single contact nobody reads would be a field fetched to be thrown away.
 */
export const UPSERT_OFFER_CONTACT_MUTATION = gql`
  mutation upsertOfferContact($input: UpsertContactMutationInput!) {
    upsertOfferContact(input: $input) {
      sheet {
        id
        contacts {
          ...OfferSheetContactFields
        }
        summary {
          ...SummaryFields
        }
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${OFFER_SHEET_CONTACT_FIELDS}
  ${SUMMARY_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const DELETE_OFFER_CONTACT_MUTATION = gql`
  mutation deleteOfferContact($input: DeleteContactMutationInput!) {
    deleteOfferContact(input: $input) {
      sheet {
        id
        contacts {
          ...OfferSheetContactFields
        }
        summary {
          ...SummaryFields
        }
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${OFFER_SHEET_CONTACT_FIELDS}
  ${SUMMARY_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const UPSERT_OFFER_EXPENSE_CATEGORY_MUTATION = gql`
  mutation upsertOfferExpenseCategory($input: UpsertExpenseCategoryMutationInput!) {
    upsertOfferExpenseCategory(input: $input) {
      expenseCategory {
        ...ExpenseCategoryFields
      }
      sheet {
        id
        expenseCategories {
          ...ExpenseCategoryFields
        }
        summary {
          ...SummaryFields
        }
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${EXPENSE_CATEGORY_FIELDS}
  ${SUMMARY_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const DELETE_OFFER_EXPENSE_CATEGORY_MUTATION = gql`
  mutation deleteOfferExpenseCategory($input: DeleteExpenseCategoryMutationInput!) {
    deleteOfferExpenseCategory(input: $input) {
      sheet {
        id
        expenseCategories {
          ...ExpenseCategoryFields
        }
        summary {
          ...SummaryFields
        }
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${EXPENSE_CATEGORY_FIELDS}
  ${SUMMARY_FIELDS}
  ${VP_ERROR_FIELDS}
`;

/**
 * The whole category list comes back, not just the touched category: the caller replaces its
 * categories wholesale rather than hunting for the one the expense belongs to.
 */
export const UPSERT_OFFER_EXPENSE_MUTATION = gql`
  mutation upsertOfferExpense($input: UpsertExpenseMutationInput!) {
    upsertOfferExpense(input: $input) {
      expense {
        ...ExpenseFields
      }
      sheet {
        id
        expenseCategories {
          ...ExpenseCategoryFields
        }
        summary {
          ...SummaryFields
        }
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${EXPENSE_FIELDS}
  ${EXPENSE_CATEGORY_FIELDS}
  ${SUMMARY_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const DELETE_OFFER_EXPENSE_MUTATION = gql`
  mutation deleteOfferExpense($input: DeleteExpenseMutationInput!) {
    deleteOfferExpense(input: $input) {
      sheet {
        id
        expenseCategories {
          ...ExpenseCategoryFields
        }
        summary {
          ...SummaryFields
        }
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${EXPENSE_CATEGORY_FIELDS}
  ${SUMMARY_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const UPSERT_OFFER_LIST_MUTATION = gql`
  mutation upsertOfferList($input: UpsertListMutationInput!) {
    upsertOfferList(input: $input) {
      list {
        ...ListFields
      }
      sheet {
        id
        listCount
        lists {
          ...ListFields
        }
        summary {
          ...SummaryFields
        }
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${LIST_FIELDS}
  ${SUMMARY_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const DELETE_OFFER_LIST_MUTATION = gql`
  mutation deleteOfferList($input: DeleteListMutationInput!) {
    deleteOfferList(input: $input) {
      sheet {
        id
        listCount
        lists {
          ...ListFields
        }
        summary {
          ...SummaryFields
        }
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${LIST_FIELDS}
  ${SUMMARY_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const UPSERT_OFFER_TICKET_MUTATION = gql`
  mutation upsertOfferTicket($input: UpsertTicketMutationInput!) {
    upsertOfferTicket(input: $input) {
      ticket {
        ...OfferTicketFields
      }
      sheet {
        id
        ticketCount
        tickets {
          ...OfferTicketFields
        }
        summary {
          ...SummaryFields
        }
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${OFFER_TICKET_FIELDS}
  ${SUMMARY_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const DELETE_OFFER_TICKET_MUTATION = gql`
  mutation deleteOfferTicket($input: DeleteTicketMutationInput!) {
    deleteOfferTicket(input: $input) {
      sheet {
        id
        ticketCount
        tickets {
          ...OfferTicketFields
        }
        summary {
          ...SummaryFields
        }
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${OFFER_TICKET_FIELDS}
  ${SUMMARY_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const CREATE_OFFER_SHEET_TEMPLATE_MUTATION = gql`
  mutation createOfferSheetTemplate($input: CreateTemplateMutationInput!) {
    createOfferSheetTemplate(input: $input) {
      template {
        ...TemplateFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${TEMPLATE_FIELDS}
  ${VP_ERROR_FIELDS}
`;

/**
 * The only sheet-returning mutation that does not select the sheet: applying a template overwrites
 * the offer wholesale, and the caller answers that by dropping its sheet and re-reading it, so
 * anything selected here would be fetched and thrown away. The payload's job is to say whether the
 * apply succeeded.
 */
export const APPLY_OFFER_SHEET_TEMPLATE_MUTATION = gql`
  mutation applyOfferSheetTemplate($input: ApplyTemplateMutationInput!) {
    applyOfferSheetTemplate(input: $input) {
      sheet {
        id
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const fetchOfferSheet = async (
  client: ApolloClient<Query>,
  id: number,
): Promise<Sheet.Sheet | undefined> => {
  const { data } = await client.query<Query>({
    query: OFFER_SHEET_QUERY,
    variables: { id },
    fetchPolicy: 'network-only',
  });

  // `offerSheet` is nullable, and the builder relies on the absence: it renders nothing at all
  // rather than an empty shell when the sheet cannot be read.
  return data.offerSheet ? Sheet.fromGql(data.offerSheet) : undefined;
};

export const fetchOfferSheetTemplates = async (client: ApolloClient<Query>): Promise<Template.Template[]> => {
  const { data } = await client.query<Query>({
    query: OFFER_SHEET_TEMPLATES_QUERY,
  });

  return (data.offerSheetTemplates ?? []).map(Template.fromGql);
};

const DEAL_FIGURES = {
  dealArtistGuarantee: DealFigureEnum.DealArtistGuarantee,
  dealGborPercentage: DealFigureEnum.DealGborPercentage,
  headlinerGuarantee: DealFigureEnum.HeadlinerGuarantee,
  supportGuarantee: DealFigureEnum.SupportGuarantee,
} as const satisfies Partial<Record<keyof UpdateSheetMutationInput, DealFigureEnum>>;

// The API ignores a null deal figure (backend ADR-012, decision 8), so an emptied one is cleared by name.
const clearNullDealFigures = (input: UpdateSheetMutationInput): UpdateSheetMutationInput => {
  const emptied = (Object.keys(DEAL_FIGURES) as Array<keyof typeof DEAL_FIGURES>).filter(field => input[field] === null);

  if (emptied.length === 0) {
    return input;
  }

  const values = { ...input };
  emptied.forEach(field => delete values[field]);
  const cleared = [...(input.clearDealFigures ?? []), ...emptied.map(field => DEAL_FIGURES[field])];

  return { ...values, clearDealFigures: [...new Set(cleared)] };
};

export const updateOfferSheet = async (
  client: ApolloClient<Mutation>,
  input: UpdateSheetMutationInput,
): Promise<Sheet.Sheet | undefined> => {
  const { data } = await client.mutate<Mutation>({
    mutation: UPDATE_OFFER_SHEET_MUTATION,
    variables: { input: clearNullDealFigures(input) },
  });

  const sheet = data?.updateOfferSheet?.sheet;

  return sheet ? Sheet.fromGql(sheet) : undefined;
};

export const lockOfferSheet = async (
  client: ApolloClient<Mutation>,
  offerSheetId: number,
): Promise<Pick<Sheet.Sheet, 'lockedAt' | 'lockedBy'> | undefined> => {
  // Typed rather than inlined: the `mutate<Mutation>` generic does not check `variables`.
  const input: LockSheetMutationInput = { offerSheetId };

  const { data } = await client.mutate<Mutation>({
    mutation: LOCK_OFFER_SHEET_MUTATION,
    variables: { input },
  });

  const sheet = data?.lockOfferSheet?.sheet;

  if (!sheet) {
    return undefined;
  }

  const { lockedAt, lockedBy } = Sheet.fromGql(sheet);

  return { lockedAt, lockedBy };
};

export const createOfferSheetVersion = async (
  client: ApolloClient<Mutation>,
  input: CreateSheetVersionMutationInput,
): Promise<SheetVersion.SheetVersion> => {
  const { data } = await client.mutate<Mutation>({
    mutation: CREATE_OFFER_SHEET_VERSION_MUTATION,
    variables: { input },
  });

  const version = data?.createOfferSheetVersion?.version;

  // The payload types `version` as non-null, so an absent one means the mutation did not resolve
  // at all. The caller destructures the result, so returning `undefined` would fail later and
  // further away than throwing here.
  if (!version) {
    throw new Error('The offer sheet version mutation returned no version.');
  }

  return SheetVersion.fromGql(version);
};

export const generateOfferSheetPerformanceDates = async (
  client: ApolloClient<Mutation>,
  offerSheetId: number,
): Promise<Pick<Sheet.Sheet, 'performanceDatesDescription' | 'summary'> | undefined> => {
  const input: GeneratePerformanceDatesMutationInput = { offerSheetId };

  const { data } = await client.mutate<Mutation>({
    mutation: GENERATE_OFFER_SHEET_PERFORMANCE_DATES_MUTATION,
    variables: { input },
  });

  const sheet = data?.generateOfferSheetPerformanceDates?.sheet;

  if (!sheet) {
    return undefined;
  }

  const { performanceDatesDescription, summary } = Sheet.fromGql(sheet);

  return { performanceDatesDescription, summary };
};

export const upsertOfferSheetAgent = async (
  client: ApolloClient<Mutation>,
  input: UpsertAgentMutationInput,
): Promise<VPResult<Pick<Sheet.Sheet, 'agent' | 'summary'>, VPErrors.VPError[]>> => {
  const { data } = await client.mutate<Mutation>({
    mutation: UPSERT_OFFER_SHEET_AGENT_MUTATION,
    variables: { input },
  });

  const payload = data?.upsertOfferSheetAgent;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  // This payload carries no `success`, so an empty error list with no sheet is the one shape that
  // would otherwise read as a silent success.
  if (!payload?.sheet) {
    return failure('Failed to save the agent.');
  }

  return {
    success: true,
    data: {
      agent: payload.sheet.agent ? Agent.fromGql(payload.sheet.agent) : undefined,
      summary: summaryOf(payload.sheet),
    },
  };
};

/**
 * The ten line-item mutations and the two template mutations. Each follows `upsertOfferSheetAgent`
 * exactly: structured errors first, then the absent-payload shape that would otherwise read as a
 * silent success, then the mapped payload.
 *
 * Collections come back as arrays, never `undefined` -- the caller assigns what it is handed and
 * must not have to tell "the sheet has no lists" from "the payload did not carry them".
 */

export const upsertOfferContact = async (
  client: ApolloClient<Mutation>,
  input: UpsertContactMutationInput,
): Promise<VPResult<ContactsResult, VPErrors.VPError[]>> => {
  const { data } = await client.mutate<Mutation>({
    mutation: UPSERT_OFFER_CONTACT_MUTATION,
    variables: { input },
  });

  const payload = data?.upsertOfferContact;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.sheet) {
    return failure('Failed to save the contact.');
  }

  return {
    success: true,
    data: {
      contacts: (payload.sheet.contacts ?? []).map(OfferSheetContact.fromGql),
      summary: summaryOf(payload.sheet),
    },
  };
};

export const deleteOfferContact = async (
  client: ApolloClient<Mutation>,
  id: number,
): Promise<VPResult<ContactsResult, VPErrors.VPError[]>> => {
  // Typed rather than inlined: the `mutate<Mutation>` generic does not check `variables`.
  const input: DeleteContactMutationInput = { id };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_OFFER_CONTACT_MUTATION,
    variables: { input },
  });

  const payload = data?.deleteOfferContact;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.sheet) {
    return failure('Failed to delete the contact.');
  }

  return {
    success: true,
    data: {
      contacts: (payload.sheet.contacts ?? []).map(OfferSheetContact.fromGql),
      summary: summaryOf(payload.sheet),
    },
  };
};

export const upsertOfferExpenseCategory = async (
  client: ApolloClient<Mutation>,
  input: UpsertExpenseCategoryMutationInput,
): Promise<
  VPResult<
    ExpenseCategoriesResult & { expenseCategory?: ExpenseCategory.ExpenseCategory },
    VPErrors.VPError[]
  >
> => {
  const { data } = await client.mutate<Mutation>({
    mutation: UPSERT_OFFER_EXPENSE_CATEGORY_MUTATION,
    variables: { input },
  });

  const payload = data?.upsertOfferExpenseCategory;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.sheet) {
    return failure('Failed to save the expense category.');
  }

  return {
    success: true,
    data: {
      expenseCategory: payload.expenseCategory ? ExpenseCategory.fromGql(payload.expenseCategory) : undefined,
      expenseCategories: (payload.sheet.expenseCategories ?? []).map(ExpenseCategory.fromGql),
      summary: summaryOf(payload.sheet),
    },
  };
};

export const deleteOfferExpenseCategory = async (
  client: ApolloClient<Mutation>,
  id: number,
): Promise<VPResult<ExpenseCategoriesResult, VPErrors.VPError[]>> => {
  const input: DeleteExpenseCategoryMutationInput = { id };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_OFFER_EXPENSE_CATEGORY_MUTATION,
    variables: { input },
  });

  const payload = data?.deleteOfferExpenseCategory;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.sheet) {
    return failure('Failed to delete the expense category.');
  }

  return {
    success: true,
    data: {
      expenseCategories: (payload.sheet.expenseCategories ?? []).map(ExpenseCategory.fromGql),
      summary: summaryOf(payload.sheet),
    },
  };
};

export const upsertOfferExpense = async (
  client: ApolloClient<Mutation>,
  input: UpsertExpenseMutationInput,
): Promise<
  VPResult<ExpenseCategoriesResult & { expense?: Expense.Expense }, VPErrors.VPError[]>
> => {
  const { data } = await client.mutate<Mutation>({
    mutation: UPSERT_OFFER_EXPENSE_MUTATION,
    variables: { input },
  });

  const payload = data?.upsertOfferExpense;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.sheet) {
    return failure('Failed to save the expense.');
  }

  return {
    success: true,
    data: {
      expense: payload.expense ? Expense.fromGql(payload.expense) : undefined,
      expenseCategories: (payload.sheet.expenseCategories ?? []).map(ExpenseCategory.fromGql),
      summary: summaryOf(payload.sheet),
    },
  };
};

export const deleteOfferExpense = async (
  client: ApolloClient<Mutation>,
  id: number,
): Promise<VPResult<ExpenseCategoriesResult, VPErrors.VPError[]>> => {
  const input: DeleteExpenseMutationInput = { id };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_OFFER_EXPENSE_MUTATION,
    variables: { input },
  });

  const payload = data?.deleteOfferExpense;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.sheet) {
    return failure('Failed to delete the expense.');
  }

  return {
    success: true,
    data: {
      expenseCategories: (payload.sheet.expenseCategories ?? []).map(ExpenseCategory.fromGql),
      summary: summaryOf(payload.sheet),
    },
  };
};

export const upsertOfferList = async (
  client: ApolloClient<Mutation>,
  input: UpsertListMutationInput,
): Promise<VPResult<ListsResult & { list?: List.List }, VPErrors.VPError[]>> => {
  const { data } = await client.mutate<Mutation>({
    mutation: UPSERT_OFFER_LIST_MUTATION,
    variables: { input },
  });

  const payload = data?.upsertOfferList;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.sheet) {
    return failure('Failed to save the list.');
  }

  return {
    success: true,
    data: {
      list: payload.list ? List.fromGql(payload.list) : undefined,
      listCount: payload.sheet.listCount ?? undefined,
      lists: (payload.sheet.lists ?? []).map(List.fromGql),
      summary: summaryOf(payload.sheet),
    },
  };
};

export const deleteOfferList = async (
  client: ApolloClient<Mutation>,
  id: number,
): Promise<VPResult<ListsResult, VPErrors.VPError[]>> => {
  const input: DeleteListMutationInput = { id };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_OFFER_LIST_MUTATION,
    variables: { input },
  });

  const payload = data?.deleteOfferList;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.sheet) {
    return failure('Failed to delete the list.');
  }

  return {
    success: true,
    data: {
      listCount: payload.sheet.listCount ?? undefined,
      lists: (payload.sheet.lists ?? []).map(List.fromGql),
      summary: summaryOf(payload.sheet),
    },
  };
};

export const upsertOfferTicket = async (
  client: ApolloClient<Mutation>,
  input: UpsertTicketMutationInput,
): Promise<
  VPResult<
    TicketsResult & { ticket?: OfferTicket.OfferTicket },
    VPErrors.VPError[]
  >
> => {
  const { data } = await client.mutate<Mutation>({
    mutation: UPSERT_OFFER_TICKET_MUTATION,
    variables: { input },
  });

  const payload = data?.upsertOfferTicket;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.sheet) {
    return failure('Failed to save the ticket.');
  }

  return {
    success: true,
    data: {
      ticket: payload.ticket ? OfferTicket.fromGql(payload.ticket) : undefined,
      ticketCount: payload.sheet.ticketCount ?? undefined,
      tickets: (payload.sheet.tickets ?? []).map(OfferTicket.fromGql),
      summary: summaryOf(payload.sheet),
    },
  };
};

export const deleteOfferTicket = async (
  client: ApolloClient<Mutation>,
  id: number,
): Promise<VPResult<TicketsResult, VPErrors.VPError[]>> => {
  const input: DeleteTicketMutationInput = { id };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_OFFER_TICKET_MUTATION,
    variables: { input },
  });

  const payload = data?.deleteOfferTicket;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.sheet) {
    return failure('Failed to delete the ticket.');
  }

  return {
    success: true,
    data: {
      ticketCount: payload.sheet.ticketCount ?? undefined,
      tickets: (payload.sheet.tickets ?? []).map(OfferTicket.fromGql),
      summary: summaryOf(payload.sheet),
    },
  };
};

export const createOfferSheetTemplate = async (
  client: ApolloClient<Mutation>,
  input: CreateTemplateMutationInput,
): Promise<VPResult<{ template: Template.Template }, VPErrors.VPError[]>> => {
  const { data } = await client.mutate<Mutation>({
    mutation: CREATE_OFFER_SHEET_TEMPLATE_MUTATION,
    variables: { input },
  });

  const payload = data?.createOfferSheetTemplate;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  // The one payload here with no sheet at all: a template is a copy of the offer, not a change to
  // it, so the template itself is the only thing that can say the mutation resolved.
  if (!payload?.template) {
    return failure('Failed to create the template.');
  }

  return { success: true, data: { template: Template.fromGql(payload.template) } };
};

export const applyOfferSheetTemplate = async (
  client: ApolloClient<Mutation>,
  input: ApplyTemplateMutationInput,
): Promise<VPResult<void, VPErrors.VPError[]>> => {
  const { data } = await client.mutate<Mutation>({
    mutation: APPLY_OFFER_SHEET_TEMPLATE_MUTATION,
    variables: { input },
  });

  const payload = data?.applyOfferSheetTemplate;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.sheet) {
    return failure('Failed to apply the template.');
  }

  // Nothing is mapped back: the caller drops its sheet and re-reads it, so a wider return would be
  // state nobody consumes.
  return { success: true, data: undefined };
};
